import { T, useGT } from 'gt-react';
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import type { RenewalRule } from '@erp/rpc';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { UnreadableRuleList } from '../UnreadableRuleList';
import { describeRenewalRule, divisionScope } from './renewalRuleText';

interface RowProps {
  rule: RenewalRule;
  /** Null hides the action: the viewer may not do it. */
  onEdit: (() => void) | null;
  onToggle: ((enabled: boolean) => void) | null;
  onDelete: (() => void) | null;
  isBusy: boolean;
}

/** One saved Renewal Rule as a compact list item, with its actions. */
export function RenewalRuleRow({
  rule,
  onEdit,
  onToggle,
  onDelete,
  isBusy,
}: RowProps) {
  const gt = useGT();
  return (
    <li className="flex items-center gap-2 border-b py-1.5 text-sm last:border-b-0">
      <Switch
        aria-label={gt('Rule on', {
          context:
            'label of the switch that turns a Mass Renewal rule on or off',
        })}
        checked={rule.enabled}
        disabled={!onToggle || isBusy}
        onCheckedChange={(enabled) => onToggle?.(enabled)}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{rule.name}</div>
        <div className="text-muted-foreground truncate text-xs">
          {describeRenewalRule(rule.rule, gt)}
          {' · '}
          {divisionScope(rule.divisions, gt)}
        </div>
      </div>
      {onEdit ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={onEdit}
          aria-label={gt('Edit rule', {
            context: 'button that edits a Mass Renewal rule',
          })}
        >
          <PencilSimpleIcon className="h-4 w-4" />
        </Button>
      ) : null}
      {onDelete ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={isBusy}
          onClick={onDelete}
          aria-label={gt('Delete rule', {
            context: 'button that deletes a Mass Renewal rule',
          })}
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      ) : null}
    </li>
  );
}

/** Stored rules this version can't read, listed so they can be deleted. */
export function UnreadableRenewalRules(props: {
  ids: string[];
  onDelete: ((id: string) => void) | null;
  isBusy: boolean;
}) {
  return (
    <UnreadableRuleList
      {...props}
      warning={
        <T context="Mass Renewal rules panel warning about stored rules this version cannot read">
          These rules were saved by another version and can’t be read. Delete
          them before running a Mass Renewal.
        </T>
      }
      deleteLabel={
        <T context="button that deletes an unreadable Mass Renewal rule">
          Delete
        </T>
      }
    />
  );
}
